import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { sendVerificationEmail, reloadUser, isEmailVerified } from '../auth/emailVerification';
import { useAuth } from '../auth/hooks';
import { HybridIcon } from '../components/HybridIcon';
import './Auth.css';

export default function VerifyEmail() {
  const { firebaseUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if email is already verified
    if (firebaseUser) {
      if (firebaseUser.emailVerified) {
        setVerified(true);
      }
    } else {
      // Not logged in, redirect to login
      navigate('/login');
    }
  }, [firebaseUser, navigate]);

  useEffect(() => {
    // Poll for email verification status
    if (firebaseUser && !firebaseUser.emailVerified) {
      const interval = setInterval(async () => {
        await reloadUser();
        if (isEmailVerified()) {
          setVerified(true);
          clearInterval(interval);
        }
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval);
    }
  }, [firebaseUser]);

  const handleResendEmail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await sendVerificationEmail();
      if (result.success) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        setError(result.error || 'Failed to send verification email');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/overview');
  };

  if (verified) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <div className="auth-header">
            <HybridIcon name="checkmark-circle" size={64} color="#4caf50" />
            <h1 className="auth-title">Email Verified!</h1>
            <p className="auth-subtitle">Your email has been successfully verified</p>
          </div>

          <div className="auth-message">
            <p className="auth-message-text">
              You can now access all features of Path Finder.
            </p>
          </div>

          <button
            onClick={handleContinue}
            className="auth-button auth-button-primary"
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-header">
          <HybridIcon name="mail" size={64} color="#f4511e" />
          <h1 className="auth-title">Verify Your Email</h1>
          <p className="auth-subtitle">Please verify your email address to continue</p>
        </div>

        <div className="auth-message">
          <p>We've sent a verification email to:</p>
          <p className="auth-email-display">{firebaseUser?.email}</p>
          <p className="auth-message-text">
            Please check your inbox and click the verification link.
            If you don't see the email, check your spam folder.
          </p>
        </div>

        {error && (
          <div className="auth-error">
            <HybridIcon name="alert-circle" size={20} color="#d32f2f" />
            <span>{error}</span>
          </div>
        )}

        {emailSent && (
          <div className="auth-success">
            <HybridIcon name="checkmark-circle" size={20} color="#4caf50" />
            <span>Verification email sent!</span>
          </div>
        )}

        <button
          onClick={handleResendEmail}
          className="auth-button auth-button-secondary"
          disabled={loading}
        >
          {loading ? (
            <div className="spinner-small" />
          ) : (
            'Resend Verification Email'
          )}
        </button>

        <div className="auth-footer">
          <Link to="/login" className="auth-link">Back to Login</Link>
        </div>
      </div>
    </div>
  );
}

